import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import * as d3 from 'd3';
import { Focus as CenterFocusStrongRounded, ZoomIn, ZoomOut } from 'lucide-react';
import Text from '@root/components/v2/ui-elements/Text';
import IconButton from '@root/components/v2/ui-elements/IconButton';
import COLORS from '@pages/visualization/ForceGraph/color-mapping';
import ICONS from '@pages/visualization/ForceGraph/icon-mapping';
import { getColorFromString } from '@pages/visualization/ForceGraph/utils';
import terraformIcon from '@assets/svg/techLogos/Terraform.svg';
import githubIcon from '@assets/svg/github.svg';
import { mapFinOpsGraphData } from './finopsGraphMapper';
// Its zoom controls are styled there (Finops__ZoomButtons); imported here so the graph renders right
// wherever it is reused, not only under the FinOps pages that load the stylesheet.
import './style.scss';

// Non-resource glyphs that aren't in the shared ICONS map.
const SPECIAL_ICONS = { terraform: terraformIcon, repository: githubIcon };

// Inline lucide glyphs (path/circle node arrays) for the branch nodes — same
// icons shown in the reference (hand-coins for cost, binoculars for finding).
const LUCIDE_ICONS = {
  'lucide-hand-coins': [
    ['path', { d: 'M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17' }],
    ['path', { d: 'm7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9' }],
    ['path', { d: 'm2 16 6 6' }],
    ['circle', { cx: '16', cy: '9', r: '2.9' }],
    ['circle', { cx: '6', cy: '5', r: '3' }],
  ],
  'lucide-binoculars': [
    ['path', { d: 'M10 10h4' }],
    ['path', { d: 'M19 7V4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v3' }],
    [
      'path',
      { d: 'M20 21a2 2 0 0 0 2-2v-3.851c0-1.39-2-2.962-2-4.829V8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v11a2 2 0 0 0 2 2z' },
    ],
    ['path', { d: 'M 22 16 L 2 16' }],
    [
      'path',
      { d: 'M4 21a2 2 0 0 1-2-2v-3.851c0-1.39 2-2.962 2-4.829V8a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v11a2 2 0 0 1-2 2z' },
    ],
    ['path', { d: 'M9 7V4a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v3' }],
  ],
  // Node and badge glyphs of the Kubernetes Findings attack path, copied from lucide-react 0.577.
  'lucide-layers': [
    [
      'path',
      {
        d: 'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z',
      },
    ],
    ['path', { d: 'M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12' }],
    ['path', { d: 'M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17' }],
  ],
  'lucide-package': [
    [
      'path',
      {
        d: 'M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z',
      },
    ],
    ['path', { d: 'M12 22V12' }],
    ['polyline', { points: '3.29 7 12 12 20.71 7' }],
    ['path', { d: 'm7.5 4.27 9 5.15' }],
  ],
  'lucide-bug': [
    ['path', { d: 'M12 20v-9' }],
    ['path', { d: 'M14 7a4 4 0 0 1 4 4v3a6 6 0 0 1-12 0v-3a4 4 0 0 1 4-4z' }],
    ['path', { d: 'M14.12 3.88 16 2' }],
    ['path', { d: 'M21 21a4 4 0 0 0-3.81-4' }],
    ['path', { d: 'M21 5a4 4 0 0 1-3.55 3.97' }],
    ['path', { d: 'M22 13h-4' }],
    ['path', { d: 'M3 21a4 4 0 0 1 3.81-4' }],
    ['path', { d: 'M3 5a4 4 0 0 0 3.55 3.97' }],
    ['path', { d: 'M6 13H2' }],
    ['path', { d: 'm8 2 1.88 1.88' }],
    ['path', { d: 'M9 7.13V6a3 3 0 1 1 6 0v1.13' }],
  ],
  'lucide-globe': [
    ['circle', { cx: '12', cy: '12', r: '10' }],
    ['path', { d: 'M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20' }],
    ['path', { d: 'M2 12h20' }],
  ],
  'lucide-arrow-up-right': [
    ['path', { d: 'M7 7h10v10' }],
    ['path', { d: 'M7 17 17 7' }],
  ],
  'lucide-shield-alert': [
    [
      'path',
      {
        d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
      },
    ],
    ['path', { d: 'M12 8v4' }],
    ['path', { d: 'M12 16h.01' }],
  ],
  'lucide-database': [
    ['ellipse', { cx: '12', cy: '5', rx: '9', ry: '3' }],
    ['path', { d: 'M3 5V19A9 3 0 0 0 21 19V5' }],
    ['path', { d: 'M3 12A9 3 0 0 0 21 12' }],
  ],
  'lucide-circle-play': [
    ['path', { d: 'M9 9.003a1 1 0 0 1 1.517-.859l4.997 2.997a1 1 0 0 1 0 1.718l-4.997 2.997A1 1 0 0 1 9 14.996z' }],
    ['circle', { cx: '12', cy: '12', r: '10' }],
  ],
  'lucide-wrench': [
    [
      'path',
      {
        d: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.106-3.105c.32-.322.863-.22.983.218a6 6 0 0 1-8.259 7.057l-7.91 7.91a1 1 0 0 1-2.999-3l7.91-7.91a6 6 0 0 1 7.057-8.259c.438.12.54.662.219.984z',
      },
    ],
  ],
  'lucide-circle-alert': [
    ['circle', { cx: '12', cy: '12', r: '10' }],
    ['line', { x1: '12', x2: '12', y1: '8', y2: '12' }],
    ['line', { x1: '12', x2: '12.01', y1: '16', y2: '16' }],
  ],
};

// Resolve a node's icon URL — mirrors graph-view.tsx's `getIconUrl`:
// `internet` -> the shared Internet-Exposed icon, brand glyphs from
// SPECIAL_ICONS, everything else from the shared resource ICONS map.
const getIconUrl = resourceType => {
  if (resourceType === 'internet') return '/images/visualizer/resource-icons/Internet_Exposed_Icon.svg';
  return SPECIAL_ICONS[resourceType] || ICONS[resourceType];
};

// Node accent color — resource color from the shared map, else the node's
// explicit accent, else a deterministic hash color (same fallbacks as the app).
const getNodeColor = node => COLORS[node.resourceType] || node.color || getColorFromString(node.resourceType);

// Full-color brand logos (internet globe, Terraform, GitHub, cost) read best on
// a white disc; the shared AWS resource icons are white glyphs and need the
// brand-colored disc behind them (exactly as graph-view.tsx fills them).
const isBrandLogo = resourceType => resourceType === 'internet' || Boolean(SPECIAL_ICONS[resourceType]);

const NODE_RADIUS = 28;
// A node may set its own `radius` (e.g. the nodes of the Overview graph); the rest use NODE_RADIUS.
const radiusOf = node => node.radius ?? NODE_RADIUS;

// The smallest box centered on `point` that still holds `box`, so centering on it lands the point mid-canvas.
const boxAround = (box, point) => {
  const halfWidth = Math.max(point.x - box.x, box.x + box.width - point.x);
  const halfHeight = Math.max(point.y - box.y, box.y + box.height - point.y);
  return { x: point.x - halfWidth, y: point.y - halfHeight, width: 2 * halfWidth, height: 2 * halfHeight };
};
const NODE_MODAL_GAP = 12; // screen px between a clicked node and its modal
// Default room the node modal needs on the node's right; when the viewport has
// less, the graph pans left first so the modal never lands on it.
const NODE_MODAL_MIN_WIDTH = 280;
const VIEWPORT_MARGIN = 16;
const NODE_PAN_DURATION = 250;
const ICON_SIZE = 30;
const INTERNET_ICON_SIZE = 48;
const LINK_COLOR = '#cbd5e0'; // default greyish connector, matching graph-view.tsx
const HOVER_LINK_COLOR = '#f62121'; // connected-link highlight, matching graph-view.tsx
const ARROW_GREY = 'finops-arrow-grey';
const ARROW_RED = 'finops-arrow-red';
const DEFAULT_ZOOM_LEVEL = 1.2;
const MAX_ZOOM_LEVEL = 2;
const MIN_ZOOM_LEVEL = 0.2;
const FIT_PADDING = 0.9; // share of the viewport the content may fill when centered
const DOT_SPACING = 22;
const DOT_RADIUS = 1;
const DOT_COLOR = '#e2e8f0';
// Optional badge row under a node's labels.
const BADGE_SIZE = 12;
const BADGE_SPACING = 18;
const BADGE_ROW_GAP = 14;
const BRANCH_PASTEL_FILLS = {
  cost: '#8FA3FF',
  vuln: '#FF8FA3',
};

// `data` is the resource-graph API payload ({ nodes, relations }). Nodes the API
// marks `clickable` call `onNodeClick(node, { x, y, flipX })` with where its modal
// should open. `isNodeModalOpen` tells a closing click (no pan) from an opening one.
// `mapData` turns `data` into the laid-out { nodes, links, badges } this draws, so
// other pages can feed the same graph their own payload; pass a stable function.
// Optional per item: `node.radius`, `node.href` (the icon's URL, in place of the
// resource icon), `node.fill` (the disc colour), `link.type: 'curve'` (a sideways S
// between the nodes; with `link.arrow: 'end'` its arrowhead takes the link's colour
// and touches the target), `link.color` (the link's resting colour; hover still
// turns it red) and, on the data, `center` (a point to center the view on).
// `defaultZoomLevel` is the zoom the graph opens and resets at, zooming out only when the graph would not fit.
const FinOpsVisualizerGraph = ({
  data,
  onNodeClick,
  isNodeModalOpen = false,
  mapData = mapFinOpsGraphData,
  modalMinWidth = NODE_MODAL_MIN_WIDTH,
  defaultZoomLevel = DEFAULT_ZOOM_LEVEL,
}) => {
  const graphData = useMemo(() => mapData(data), [data, mapData]);
  // Read through a ref so a new callback doesn't redraw the whole graph.
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;
  const isNodeModalOpenRef = useRef(isNodeModalOpen);
  isNodeModalOpenRef.current = isNodeModalOpen;
  const defaultZoomLevelRef = useRef(defaultZoomLevel);
  defaultZoomLevelRef.current = defaultZoomLevel;
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomRef = useRef(null);
  const d3SvgRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(defaultZoomLevel);

  const contentBoxRef = useRef(null); // bounding box of the drawn graph, for centering

  // Centers the drawn content in the viewport at defaultZoomLevel, zooming out
  // only as far as needed when that would not fit.
  const initialTransformFor = (width, height) => {
    const box = contentBoxRef.current;
    if (!box || !box.width || !box.height) return d3.zoomIdentity.scale(defaultZoomLevelRef.current);
    const k = Math.max(
      MIN_ZOOM_LEVEL,
      Math.min(defaultZoomLevelRef.current, (width * FIT_PADDING) / box.width, (height * FIT_PADDING) / box.height),
    );
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    return d3.zoomIdentity.translate(width / 2 - cx * k, height / 2 - cy * k).scale(k);
  };

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !graphData) return;

    const container = containerRef.current;
    const width = container.clientWidth || 900;
    const height = container.clientHeight || 460;

    // Clear previous render.
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current).attr('width', width).attr('height', height);
    const defs = svg.append('defs');

    // Dotted-grid background.
    const pattern = defs
      .append('pattern')
      .attr('id', 'finops-dot-pattern')
      .attr('width', DOT_SPACING)
      .attr('height', DOT_SPACING)
      .attr('patternUnits', 'userSpaceOnUse');
    pattern
      .append('circle')
      .attr('cx', DOT_SPACING / 2)
      .attr('cy', DOT_SPACING / 2)
      .attr('r', DOT_RADIUS)
      .attr('fill', DOT_COLOR);
    svg.append('rect').attr('width', width).attr('height', height).attr('fill', 'url(#finops-dot-pattern)');

    // Drop-shadow for node circles (same as graph-view.tsx).
    defs.append('filter').attr('id', 'finops-drop-shadow').attr('height', '130%').html(`
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.1"/>
    `);

    // Arrowhead markers: default grey and hover red, plus one per colour of the
    // curve links that end in an arrow, solid even on a translucent link (named
    // by the colour, so graphs sharing the page can share them too).
    const appendArrowMarker = (id, color) =>
      defs
        .append('marker')
        .attr('id', id)
        .attr('viewBox', '0 0 10 10')
        .attr('refX', 9)
        .attr('refY', 5)
        .attr('markerWidth', 7)
        .attr('markerHeight', 7)
        .attr('markerUnits', 'userSpaceOnUse')
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M 0 0 L 10 5 L 0 10 z')
        .attr('fill', color);
    appendArrowMarker(ARROW_GREY, LINK_COLOR);
    appendArrowMarker(ARROW_RED, HOVER_LINK_COLOR);
    const colorArrowFor = color => {
      const solid = d3.color(color)?.copy({ opacity: 1 }).formatRgb() ?? color;
      const id = `finops-arrow-${solid.replace(/[^\da-z]/gi, '')}`;
      if (defs.select(`#${id}`).empty()) appendArrowMarker(id, solid);
      return id;
    };

    // Zoomable root group.
    const g = svg.append('g');
    const zoom = d3
      .zoom()
      .scaleExtent([MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL])
      .on('zoom', event => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
      });
    svg.call(zoom);
    zoomRef.current = zoom;
    d3SvgRef.current = svg;

    const nodeMap = {};
    graphData.nodes.forEach(n => {
      nodeMap[n.id] = n;
    });

    // Track drawn elements so hover can highlight the connected links.
    const linkEls = [];
    const nodeEls = {};

    // --- Links ---------------------------------------------------------------
    graphData.links.forEach(link => {
      const source = nodeMap[link.source];
      const target = nodeMap[link.target];
      if (!source || !target) return;
      const sourceRadius = radiusOf(source);
      const targetRadius = radiusOf(target);
      const color = link.color ?? LINK_COLOR;

      // Branch links leave the lower edge of each branch circle and land at
      // separate points on EC2's upper edge. This makes both connections read
      // as attached to the parent, rather than as elbows skimming its top.
      if (link.type === 'branch') {
        const sx = source.x;
        const sy = source.y + sourceRadius;
        const isLeftBranch = source.x < target.x;
        const portOffset = isLeftBranch ? -targetRadius * 0.55 : targetRadius * 0.55;
        const ex = target.x + portOffset;
        const ey = target.y - Math.sqrt(targetRadius ** 2 - portOffset ** 2);
        const controlDistance = Math.max(28, (ey - sy) * 0.55);
        const d = `M ${sx} ${sy} C ${sx} ${sy + controlDistance} ${ex} ${ey - controlDistance} ${ex} ${ey}`;
        const path = g
          .append('path')
          .attr('d', d)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', 2)
          .attr('stroke-linecap', 'round');
        linkEls.push({ el: path, sourceId: link.source, targetId: link.target, hasArrow: false, color });
        return;
      }

      // Curve links leave the side of one node facing the other and run across to
      // it, bending into an S where the two sit at different heights.
      // Arrowed, its tip touches the target.
      if (link.type === 'curve') {
        const arrowId = link.arrow === 'end' ? colorArrowFor(color) : null;
        const direction = Math.sign(target.x - source.x) || 1;
        const sx = source.x + direction * (sourceRadius + 2);
        const ex = target.x - direction * (targetRadius + (arrowId ? 1 : 2));
        const midX = (sx + ex) / 2;
        const path = g
          .append('path')
          .attr('d', `M ${sx} ${source.y} C ${midX} ${source.y} ${midX} ${target.y} ${ex} ${target.y}`)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', 2)
          .attr('stroke-linecap', 'round')
          .attr('marker-end', arrowId ? `url(#${arrowId})` : null);
        linkEls.push({
          el: path,
          sourceId: link.source,
          targetId: link.target,
          hasArrow: Boolean(arrowId),
          arrowId,
          color,
        });
        return;
      }

      // Trim the segment to each node's edge so lines meet the circles cleanly.
      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      const gap = link.arrow === 'end' ? targetRadius + 8 : targetRadius + 2;

      const hasArrow = link.arrow === 'end';
      const line = g
        .append('line')
        .attr('x1', source.x + ux * (sourceRadius + 2))
        .attr('y1', source.y + uy * (sourceRadius + 2))
        .attr('x2', target.x - ux * gap)
        .attr('y2', target.y - uy * gap)
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('marker-end', hasArrow ? `url(#${ARROW_GREY})` : null);
      linkEls.push({ el: line, sourceId: link.source, targetId: link.target, hasArrow, color });
    });

    // --- Cost badges ---------------------------------------------------------
    (graphData.badges || []).forEach(badge => {
      const isNeg = badge.variant === 'negative';
      const badgeFill = isNeg ? '#d1fadf' : '#2f6df6';
      const badgeText = isNeg ? '#16794c' : '#ffffff';
      const badgeGroup = g.append('g').attr('transform', `translate(${badge.x}, ${badge.y})`);
      const w = 56;
      const h = 24;
      badgeGroup
        .append('rect')
        .attr('x', -w / 2)
        .attr('y', -h / 2)
        .attr('width', w)
        .attr('height', h)
        .attr('rx', 5)
        .attr('fill', badgeFill);
      badgeGroup
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('fill', badgeText)
        .attr('font-size', '13px')
        .attr('font-weight', '700')
        .attr('font-family', 'Manrope, sans-serif')
        .text(badge.text);
    });

    // Append an inline lucide glyph (stroke-based) centered in a node.
    const appendLucideIcon = (group, iconNode, size, color, strokeWidth = 1) => {
      const scale = size / 24;
      const ig = group.append('g').attr('transform', `translate(${-size / 2}, ${-size / 2}) scale(${scale})`);
      iconNode.forEach(([tag, attrs]) => {
        const el = ig.append(tag);
        Object.entries(attrs).forEach(([k, v]) => el.attr(k, v));
        el.attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', strokeWidth)
          .attr('stroke-linecap', 'round')
          .attr('stroke-linejoin', 'round');
      });
      return ig;
    };

    // --- Nodes ---------------------------------------------------------------
    graphData.nodes.forEach(node => {
      const color = getNodeColor(node);
      const radius = radiusOf(node);
      // Icons grow with the disc; at NODE_RADIUS they keep their usual sizes.
      const iconScale = radius / NODE_RADIUS;
      const isInternet = node.resourceType === 'internet';
      const isLucide = typeof node.icon === 'string' && LUCIDE_ICONS[node.icon];
      // Brand logos retain their white disc. The insight nodes use soft pastel
      // fills, while their accent remains visible in the icon and border.
      const lightDisc = isBrandLogo(node.resourceType);
      const fill =
        node.fill || (isLucide ? BRANCH_PASTEL_FILLS[node.accent] || '#eef2ff' : lightDisc ? '#ffffff' : color);
      const iconSize = (isInternet ? INTERNET_ICON_SIZE : ICON_SIZE) * iconScale;
      const nodeGroup = g
        .append('g')
        .attr('transform', `translate(${node.x}, ${node.y})`)
        .style('cursor', node.clickable ? 'pointer' : 'default')
        // Lets the node modal's click-outside handler leave the node's own toggle alone.
        .attr('data-finops-clickable-node', node.clickable ? 'true' : null);

      // The Internet entry node is just the globe icon — no disc/ring.
      let circle = null;
      if (!isInternet) {
        circle = nodeGroup
          .append('circle')
          .attr('r', radius)
          .attr('fill', fill)
          .attr('stroke', color)
          .attr('stroke-width', lightDisc || isLucide ? 1 : 1)
          .style('filter', 'url(#finops-drop-shadow)');
      }

      // Icon centered in the disc — inline lucide glyph or shared resource icon.
      let image;
      if (isLucide) {
        image = appendLucideIcon(nodeGroup, LUCIDE_ICONS[node.icon], 30 * iconScale, color);
      } else {
        image = nodeGroup
          .append('image')
          .attr('href', node.href ?? getIconUrl(node.resourceType))
          .attr('width', iconSize)
          .attr('height', iconSize)
          .attr('x', -iconSize / 2)
          .attr('y', -iconSize / 2);
      }

      // Count badge (e.g. the critical-vulnerability count) at the top-right.
      if (node.count) {
        const badge = nodeGroup.append('g').attr('transform', `translate(${radius - 6}, ${-(radius - 6)})`);
        badge.append('circle').attr('r', 9).attr('fill', '#ef4444').attr('stroke', '#ffffff').attr('stroke-width', 1.5);
        badge
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('fill', '#ffffff')
          .attr('font-size', '11px')
          .attr('font-weight', '700')
          .attr('font-family', 'Manrope, sans-serif')
          .text(node.count);
      }

      // Keep the branch labels on white so a connector behind the node cannot
      // show through the text.
      if (isLucide) {
        const longestLabel = Math.max(node.label?.length || 0, node.sublabel?.length || 0);
        const labelWidth = Math.max(78, longestLabel * 6.5 + 14);
        nodeGroup
          .append('rect')
          .attr('x', -labelWidth / 2)
          .attr('y', radius + 5)
          .attr('width', labelWidth)
          .attr('height', node.sublabel ? 37 : 20)
          .attr('rx', 3)
          .attr('fill', '#ffffff');
      }

      // Label — line 1 (bold) below the node, optional line 2.
      nodeGroup
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('y', radius + 18)
        .attr('fill', isInternet ? '#1a202c' : '#475569')
        .attr('font-size', isInternet ? '13px' : '12px')
        .attr('font-weight', isInternet ? '700' : '600')
        .attr('font-family', 'Manrope, sans-serif')
        .text(node.label);
      if (node.sublabel) {
        nodeGroup
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('y', radius + 33)
          .attr('fill', '#94a3b8')
          .attr('font-size', '11px')
          .attr('font-weight', '500')
          .attr('font-family', 'Manrope, sans-serif')
          .text(node.sublabel);
      }

      // Optional row of small icons under the labels (e.g. the risks of a Findings
      // attack path node): a lucide glyph or an image each, named by a native tooltip.
      if (node.badges?.length) {
        const rowY = radius + (node.sublabel ? 33 : 18) + BADGE_ROW_GAP;
        const startX = (-(node.badges.length - 1) * BADGE_SPACING) / 2;
        node.badges.forEach((badge, index) => {
          const badgeGroup = nodeGroup
            .append('g')
            .attr('transform', `translate(${startX + index * BADGE_SPACING}, ${rowY})`);
          if (badge.label) badgeGroup.append('title').text(badge.label);
          if (badge.href) {
            badgeGroup
              .append('image')
              .attr('href', badge.href)
              .attr('width', BADGE_SIZE)
              .attr('height', BADGE_SIZE)
              .attr('x', -BADGE_SIZE / 2)
              .attr('y', -BADGE_SIZE / 2);
          } else if (LUCIDE_ICONS[badge.icon]) {
            appendLucideIcon(badgeGroup, LUCIDE_ICONS[badge.icon], BADGE_SIZE, badge.color, 2);
          }
        });
      }

      nodeEls[node.id] = { group: nodeGroup, circle, image };
    });

    // --- Hover: turn the links connected to the hovered node red (arrowheads
    // included); everything else stays as-is. No node scaling or dimming.
    graphData.nodes.forEach(node => {
      const ref = nodeEls[node.id];

      ref.group
        .on('mouseover', () => {
          linkEls.forEach(l => {
            const connected = l.sourceId === node.id || l.targetId === node.id;
            if (!connected) return;
            l.el
              .transition()
              .duration(150)
              .attr('stroke', HOVER_LINK_COLOR)
              .attr('marker-end', l.hasArrow ? `url(#${ARROW_RED})` : null);
          });
        })
        .on('mouseout', () => {
          linkEls.forEach(l => {
            l.el
              .transition()
              .duration(150)
              .attr('stroke', l.color)
              .attr('marker-end', l.hasArrow ? `url(#${l.arrowId ?? ARROW_GREY})` : null);
          });
        });

      if (node.clickable) {
        // Anchor beside the node (disc, count badge and label), not at the cursor,
        // so the modal never covers it: top-aligned with the disc, just right of
        // the node — or left of it (`flipX`) if even a pan can't make room.
        const emitClick = () => {
          const nodeRect = ref.group.node().getBoundingClientRect();
          const discRect = (ref.circle ?? ref.image).node().getBoundingClientRect();
          onNodeClickRef.current?.(node, {
            x: nodeRect.right + NODE_MODAL_GAP,
            y: discRect.top - NODE_MODAL_GAP,
            flipX: nodeRect.left - NODE_MODAL_GAP,
          });
        };
        ref.group.on('click', event => {
          event.stopPropagation();
          if (isNodeModalOpenRef.current) return emitClick(); // closing: leave the graph as is

          // Not enough room on the right: pan the graph left (no further than
          // keeps the node inside the canvas), then open the modal beside it.
          const nodeRect = ref.group.node().getBoundingClientRect();
          const roomRight = window.innerWidth - VIEWPORT_MARGIN - (nodeRect.right + NODE_MODAL_GAP);
          const roomLeft = nodeRect.left - container.getBoundingClientRect().left - VIEWPORT_MARGIN;
          const shift = Math.min(modalMinWidth - roomRight, roomLeft);
          if (shift <= 0) return emitClick();
          svg
            .transition()
            .duration(NODE_PAN_DURATION)
            .call(zoom.translateBy, -shift / d3.zoomTransform(svg.node()).k, 0)
            .on('end interrupt', emitClick);
          return undefined;
        });
      }
    });

    // Center once everything is drawn, so the bounding box covers it all (around
    // the data's `center` point, when it names one).
    const drawnBox = g.node().getBBox();
    contentBoxRef.current = graphData.center ? boxAround(drawnBox, graphData.center) : drawnBox;
    svg.call(zoom.transform, initialTransformFor(width, height));
  }, [graphData, modalMinWidth]);

  // Zoom controls — same behaviour as attack-path Graph.tsx (scaleTo in 20%
  // steps, clamped to [MIN, MAX]; reset re-centers with the initial transform).
  const handleZoomInClick = () => {
    const svg = d3SvgRef.current;
    const zoom = zoomRef.current;
    if (!svg || !zoom) return;
    const next = Math.min(zoomLevel + 0.2, MAX_ZOOM_LEVEL);
    svg.transition().duration(200).call(zoom.scaleTo, next);
  };

  const handleZoomOutClick = () => {
    const svg = d3SvgRef.current;
    const zoom = zoomRef.current;
    if (!svg || !zoom) return;
    const next = Math.max(zoomLevel - 0.2, MIN_ZOOM_LEVEL);
    svg.transition().duration(200).call(zoom.scaleTo, next);
  };

  const handleResetZoomClick = () => {
    const svg = d3SvgRef.current;
    const zoom = zoomRef.current;
    const container = containerRef.current;
    if (!svg || !zoom || !container) return;
    const width = container.clientWidth || 900;
    const height = container.clientHeight || 460;
    svg.transition().duration(300).call(zoom.transform, initialTransformFor(width, height));
  };

  if (!graphData) return null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
        <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      </Box>

      {/* Zoom controls — mirrors AttackPathAnalysis__ZoomButtons */}
      <Box
        className="Finops__ZoomButtons"
        display="flex"
        flexDirection="column"
        position="absolute"
        left="16px"
        bottom="16px">
        <IconButton width="32px" height="32px" onClick={handleResetZoomClick}>
          <CenterFocusStrongRounded size={16} />
        </IconButton>

        <IconButton width="32px" height="32px" onClick={handleZoomInClick}>
          <ZoomIn size={16} />
        </IconButton>

        <Box className="Finops__ZoomLevel">
          <Text className="fs__-1">{Math.round(zoomLevel * 100)}%</Text>
        </Box>

        <IconButton width="32px" height="32px" onClick={handleZoomOutClick}>
          <ZoomOut size={16} />
        </IconButton>
      </Box>
    </div>
  );
};

export default FinOpsVisualizerGraph;
